/* eslint-disable no-useless-catch */

import axios, { type AxiosRequestConfig } from "axios";
import fetch from "isomorphic-unfetch";

import type { IQuizz } from "@/components/v0/quizz";
import { ACCESS_LEVELS } from "@/lib/access-levels";
import { getErrorMessage } from "@/lib/error-message";
import type { GiftCardToBeBought } from "@/interfaces/order";
import {
  requestIaCreditsReservationStatus,
  type IaCreditsReservationLookup,
} from "@/lib/ia-credits-client";

import AuthService from "../auth";

import type { Proofs } from "@/context/useUses";
import type { SupportedLocale } from "@/i18n/constants";
import type {
  ChangePlayersClassesBatchPayload,
  ChangePlayersClassesBatchResponse,
} from "@/lib/bulk-class-move";
import type { PurchaseStatusSnapshot } from "@/lib/purchase-status-reconciliation";
import type { ActivityCoverDefaults } from "@/lib/activity-cover-defaults";
import { chunkPlayerIds } from "@/lib/player-ownership-counts";
import type {
  TeacherCoinLimitPreviewInput,
  TeacherCoinLimitSnapshot,
} from "@/lib/teacher-coin-limits";
import type { BlankQrCodeJobSnapshot } from "@/lib/blank-qr-codes";

import {
  getActivityCoverDefaults as requestActivityCoverDefaults,
  setActivityCoverDefaults as requestSetActivityCoverDefaults,
} from "./activity-cover-defaults";

export const BASE_URL = process.env.NEXT_PUBLIC_API_V2;

export type {
  ChangePlayersClassesBatchPayload,
  ChangePlayersClassesBatchResponse,
} from "@/lib/bulk-class-move";

export default class Client {
  public baseUrl: string = BASE_URL || "";
  static authenticatedRequest = new Client().authenticatedRequestOnAxios;
  async authenticatedRequest(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, data = false as any, queryParams: Record<string, any> = false as any, customHeaders: Record<string, string> = {}): Promise<any> {
    let headers = {
      "Content-Type": "application/json",
    } as Record<string, string>;

    const token = AuthService.isLogged();
    if (token) {
      headers = { ...headers, Authorization: token };
    }
    headers = { ...headers, ...customHeaders };
    let config = {
      method,
      headers,
    } as Record<string, any>;
    if (data) {
      config = { ...config, body: JSON.stringify(data) };
    }
    let queryParamsString = "";
    if (queryParams) {
      const built = new URLSearchParams(queryParams).toString();
      if (built) {
        queryParamsString = `?${built}`;
      }
    }
    try {
      const response = await fetch(`${BASE_URL}/${path}${queryParamsString}`, config);
      const responseJSON = await response.json();
      if (response.ok) {
        return responseJSON;
      }
      return {
        ...responseJSON,
        error: responseJSON,
        response: responseJSON,
        statusCode: response.status,
      };
    } catch (error) {
      return {
        error: error.message,
        transportError: true,
      };
    }
  }

  async authenticatedRequestOnAxios({
    method,
    path,
    data,
    headers,
    onUploadProgress,
  }: {
    method: AxiosRequestConfig["method"];
    path: string;
    data?: any;
    headers?: AxiosRequestConfig["headers"];
    onUploadProgress?: AxiosRequestConfig["onUploadProgress"];
  }) {
    let authHeaders = {
      "Content-Type": "application/json",
    } as AxiosRequestConfig["headers"];
    const token = AuthService.isLogged();
    if (token) {
      authHeaders = { ...authHeaders, Authorization: token };
    }

    const response = await axios.request({
      url: `${BASE_URL}/${path}`,
      method: method,
      headers: {
        ...authHeaders,
        ...headers,
      },
      data: data,
      responseType: "text",
      onUploadProgress,
    });
    return response;
  }

  async getIaCreditsReservationStatus(
    operationId: string,
    signal?: AbortSignal
  ): Promise<IaCreditsReservationLookup> {
    return requestIaCreditsReservationStatus({
      apiBaseUrl: this.baseUrl,
      authorization: String(AuthService.isLogged() || ""),
      operationId,
      signal,
    });
  }

  async requestAxios(formdata) {
    try {
      const token = AuthService.isLogged();
      let config = null;
      if (token) {
        config = { headers: { Authorization: token } };
      }

      const resp = await axios.post(`${BASE_URL}/shopping/items/image/create`, formdata, config);
      return resp.data;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Sessão deslizante: pede ao backend um token novo (validade renovada) usando
   * o token atual do header Authorization. O backend aceita token com data
   * vencida desde que a assinatura seja válida e o usuário ainda exista.
   */
  async renewSession() {
    const response = await this.authenticatedRequest("POST", "auth/renew");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async changeSessionOnAccount({ token, accountId, world }) {
    const response = await this.authenticatedRequest("POST", `change-session-on-account`, {
      token,
      accountId,
      world,
    });
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getBillingUrl() {
    const response = await this.authenticatedRequest("POST", `users/get-billing-url`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getPlanChangeUrl(data: {
    subscriptionId: string;
    subscriptionItemId?: string;
    priceId?: string;
    quantity?: number;
    returnUrl: string;
  }) {
    const response = await this.authenticatedRequest("POST", "users/get-plan-change-url", data);
    if (response.error) {
      throw response.error;
    }

    let portalUrl: URL;
    try {
      portalUrl = new URL(response.url);
    } catch {
      throw new Error("URL inválida do portal Stripe.");
    }

    if (portalUrl.protocol !== "https:" || portalUrl.hostname !== "billing.stripe.com") {
      throw new Error("URL inválida do portal Stripe.");
    }

    return response;
  }

  async auth({ username, password, preferrAccount }) {
    const response = await this.authenticatedRequest("POST", `auth`, {
      username,
      password,
      preferrAccount,
    });
    return response;
  }

  async authGoogle({ credential, preferrAccount }) {
    const response = await this.authenticatedRequest("POST", `auth/google`, {
      credential,
      preferrAccount,
    });
    return response;
  }

  async authApple({ code, id_token, preferrAccount }: { code: string; id_token: string; preferrAccount?: string }) {
    const response = await this.authenticatedRequest("POST", `auth/apple`, {
      code,
      id_token,
      preferrAccount,
    });
    return response;
  }

  /**
   * Login por código de email (sem senha): envia um código de 6 dígitos para o
   * email cadastrado. Lança o erro do backend (404 conta não encontrada, 502
   * falha de envio) para o caller exibir mensagem descritiva.
   */
  async sendLoginCode(email: string, locale?: SupportedLocale) {
    const response = await this.authenticatedRequest("POST", "auth/email-code/send", {
      email,
      locale,
    });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  /** Valida o código de login e devolve a sessão (mesmo shape do POST /auth). */
  async verifyLoginCode(email: string, code: string) {
    const response = await this.authenticatedRequest("POST", "auth/email-code/verify", {
      email,
      code,
    });
    return response;
  }

  async reAuth({ preferrAccount }) {
    const response = await this.authenticatedRequest("POST", `reauth`, {
      preferrAccount,
    });
    return response;
  }

  async getPlayers() {
    const response = await this.authenticatedRequest("GET", "players");
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getPlayerOwnershipCounts(playerIds: string[]): Promise<Record<string, number>> {
    const counts: Record<string, number> = {};

    for (const playerIdBatch of chunkPlayerIds(playerIds)) {
      const response = await this.authenticatedRequest("POST", "game/ownership/counts", {
        playerIds: playerIdBatch,
      });

      if (!response?.error && response && typeof response === "object" && !Array.isArray(response)) {
        Object.assign(counts, response);
        continue;
      }

      const error = new Error("Não foi possível carregar as contagens da coleção.");
      Object.assign(error, { statusCode: response?.statusCode });
      throw error;
    }

    return counts;
  }

  async getPlayersLight() {
    const response = await this.authenticatedRequest("GET", "players/light");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getPlayersByTeacher() {
    const response = await this.authenticatedRequest("GET", `players/teacher`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getPlayersLightByTeacher() {
    const response = await this.authenticatedRequest("GET", "players/light/teacher");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getSomePlayers(playersIds: string[]) {
    const response = await this.authenticatedRequest("POST", "some-players", {
      playersIds: playersIds,
    });
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getSomePlayersByTeacher(playersIds: string[]) {
    const response = await this.authenticatedRequest("POST", `some-players/teacher`, {
      playersIds: playersIds,
    });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getPlayer(pid) {
    const response = await this.authenticatedRequest("GET", `players/show/${pid}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async getPlayerByHash(hash) {
    const response = await this.authenticatedRequest("GET", `players/by-hash/${hash}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getClasses() {
    const response = await this.authenticatedRequest("GET", "classes");
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getClass(pid) {
    const response = await this.authenticatedRequest("GET", `classes/show/${pid}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async postCreateClass(data) {
    const response = await this.authenticatedRequest("POST", `classes/create`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async deleteHistory(stackedIds: string[]) {
    const response = await this.authenticatedRequest("POST", `users/transaction/delete`, {
      stackedIds,
    });
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postGetHistory() {
    try {
      const response = await this.authenticatedRequest("POST", `users/history`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAllHistory() {
    try {
      const response = await this.authenticatedRequest("POST", `users/history`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getHistoryMeta(): Promise<{ minDate: string | null; maxDate: string | null; totalChunks: number }> {
    const response = await this.authenticatedRequest("GET", `users/history/meta`);
    if (response?.error) throw new Error(response.error);
    return response;
  }

  async getHistoryChunk(from: string, to: string): Promise<any[]> {
    const response = await this.authenticatedRequest(
      "GET",
      `users/history/chunk`,
      false,
      { from, to }
    );
    if (response?.error) throw new Error(response.error);
    return Array.isArray(response) ? response : [];
  }

  async getRecurrences() {
    const response = await this.authenticatedRequest("GET", `users/recurrence`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postTempUnlock() {
    const response = await this.authenticatedRequest("POST", `users/temp-unlock`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async putUpdateClass(pid, data) {
    const response = await this.authenticatedRequest("PUT", `classes/update/${pid}`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async deleteClass(pid) {
    const response = await this.authenticatedRequest("DELETE", `classes/delete/${pid}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getGames() {
    type Games = {
      id: string;
      name: string;
      coinsLimit?: number;
      points: {
        coin: number;
        newxp: number;
        planet: number;
      };
    };

    const response = await this.authenticatedRequest("GET", "games");
    if (!response.error) {
      return response as Games[];
    }
    return [];
  }

  async getGamesStrict() {
    const response = await this.authenticatedRequest("GET", "games");
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getGamesInWorld() {
    const response = await this.authenticatedRequest("GET", "games/list-levels");
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getGameLevelsInWorld(id) {
    const response = await this.authenticatedRequest("GET", `games/${id}/levels`);
    if (!response.error) {
      return response;
    }
    return [];
  }

  async deleteGame(id) {
    const response = await this.authenticatedRequest("DELETE", `games/${id}`);
    if (!response.error) {
      return response;
    }
    return {};
  }

  async putGamesInWorld(data) {
    const response = await this.authenticatedRequest("PUT", `games/update-world-games`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postCreateHash(playerId, hash) {
    const response = await this.authenticatedRequest("POST", `players/create-hash/${playerId}`, { hash });
    if (!response.error) {
      return response;
    }
    return {};
  }

  async postCreatePlayer(data) {
    const response = await this.authenticatedRequest("POST", `players/create`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postCreatePlayersBatch(players: any[]) {
    const response = await this.authenticatedRequest("POST", "players/create-batch", { players });
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postCreatePlayersBatchStream(
    players: any[],
    onProgress: (data: { current: number; total: number; successCount: number; errorCount: number }) => void
  ): Promise<any> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/x-ndjson",
    };

    const token = AuthService.isLogged();
    if (token) {
      headers.Authorization = token;
    }

    const response = await fetch(`${BASE_URL}/players/create-batch`, {
      method: "POST",
      headers,
      body: JSON.stringify({ players }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData.error || errorData;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Stream não suportado pelo navegador");
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let finalResult: any = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const parsed = JSON.parse(line);
          if (parsed.type === "progress") {
            onProgress({
              current: parsed.current,
              total: parsed.total,
              successCount: parsed.successCount,
              errorCount: parsed.errorCount,
            });
          } else if (parsed.type === "result") {
            finalResult = parsed;
          }
        } catch {
          // ignore malformed lines
        }
      }
    }

    if (buffer.trim()) {
      try {
        const parsed = JSON.parse(buffer);
        if (parsed.type === "result") {
          finalResult = parsed;
        }
      } catch {
        // ignore
      }
    }

    if (!finalResult) {
      throw new Error("Resposta de streaming incompleta");
    }

    return finalResult;
  }

  async putUpdatePlayer(pid, data) {
    const response = await this.authenticatedRequest("PUT", `players/update/${pid}`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postChangePlayersClassesBatch(
    data: ChangePlayersClassesBatchPayload
  ): Promise<ChangePlayersClassesBatchResponse> {
    const response = await this.authenticatedRequest("POST", "players/change-classes-batch", data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async putUpdatePlayerLogs(type, data) {
    const response = await this.authenticatedRequest("PUT", `players/update-logs`, {
      type,
      data,
    });
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async deletePlayer(pid) {
    const response = await this.authenticatedRequest("DELETE", `players/delete/${pid}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getAttitudes() {
    const response = await this.authenticatedRequest("GET", `attitudes`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async getCompleteMissions(gameId, playerId) {
    const response = await this.authenticatedRequest("GET", `players/complete-missions/${gameId}/${playerId}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async getAttitude(id) {
    const response = await this.authenticatedRequest("GET", `attitudes/show/${id}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async reorderAttitudes(payload: { positiveAttitudeIds: string[]; negativeAttitudeIds: string[] }) {
    const response = await this.authenticatedRequest("PUT", "attitudes/reorder", payload);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postCreateAttitude(data, query?: any) {
    const response = await this.authenticatedRequest("POST", `attitudes/create`, data, query);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async putUpdateAttitude(pid, data) {
    const response = await this.authenticatedRequest("PUT", `attitudes/update/${pid}`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async deleteAttitude(pid) {
    const response = await this.authenticatedRequest("DELETE", `attitudes/delete/${pid}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getAttitudesByGame(game) {
    const response = await this.authenticatedRequest("GET", `attitudes/game/${game}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async postAttitudesOnGame(game, data, query) {
    const response = await this.authenticatedRequest("POST", `attitudes/apply/${game}`, data, query);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getItems() {
    const response = await this.authenticatedRequest("GET", `shopping/items`);
    if (!response.error) {
      return Array.isArray(response) ? response : [];
    }
    return [];
  }

  async postCreateItem(data) {
    const response = await this.authenticatedRequest("POST", `shopping/items/create`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async createImageItem(data) {
    const formdata = new FormData();
    formdata.append("file", data.file);
    try {
      const response = await this.requestAxios(formdata);
      return response;
    } catch (e) {
      throw e;
    }
  }

  async uploadFile(file: File, context?: string): Promise<{ url?: string; file?: string }> {
    const formdata = new FormData();
    if (context) formdata.append("context", context);
    formdata.append("file", file);

    const token = AuthService.isLogged();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = token;

    const resp = await axios.post(`${BASE_URL}/uploads/file`, formdata, { headers });
    return resp.data;
  }

  async getItemById(id) {
    const response = await this.authenticatedRequest("GET", `shopping/items/show/${id}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async putUpdateItem(pid, data) {
    if (!data.startDate) {
      data.startDate = new Date();
    }
    const response = await this.authenticatedRequest("PUT", `shopping/items/update/${pid}`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async deleteItem(pid) {
    const response = await this.authenticatedRequest("DELETE", `shopping/items/delete/${pid}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getShoppingPurchases() {
    const response = await this.authenticatedRequest("GET", `shopping/purchases`);
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getShoppingPurchasesCountTotal(): Promise<number> {
    const response = await this.authenticatedRequest("GET", `shopping/purchases/count-total`);
    if (!response.error) {
      return response.count ?? 0;
    }
    return 0;
  }

  async getShoppingPurchasesPaginated(page: number = 1, limit: number = 1000, status?: string[], throwOnError: boolean = false): Promise<{
    data: any[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
    fetchedCount?: number;
  }> {
    // status (opcional, aditivo): restringe por status no servidor — a tela de
    // pedidos carrega created/accepted primeiro sem baixar o histórico todo.
    const queryParams: Record<string, string | number> = { page, limit };
    if (status && status.length > 0) {
      queryParams.status = status.join(",");
    }
    const response = await this.authenticatedRequest("GET", `shopping/some-purchases`, undefined, queryParams);
    if (!response.error) {
      return response;
    }
    if (throwOnError) {
      throw new Error(typeof response.error === "string" ? response.error : "Falha ao carregar pedidos de recompensas");
    }
    return { data: [], total: 0, page: 1, limit, hasMore: false };
  }

  async getShoppingPurchasesCount(status: string): Promise<number> {
    const response = await this.authenticatedRequest("GET", `shopping/purchases/count/${status}`);
    if (!response.error) {
      return response.count;
    }
    return 0;
  }

  async getShoppingPurchase(id) {
    const response = await this.authenticatedRequest("GET", `shopping/purchases/show/${id}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async createShoppingPurchase(data) {
    if (!data.startDate) {
      data.startDate = new Date();
    }
    const response = await this.authenticatedRequest("POST", `shopping/purchases/create`, {
      data,
    });
    if (!response.error) {
      return response;
    }
    return null;
  }

  async checkGiftPurchases(orderIds: string[], targetStatus: "accepted" | "delivered") {
    const response = await this.authenticatedRequest("POST", "shopping/purchases/gift-preflight", { orderIds, targetStatus });
    if (response?.error) throw response;
    return response;
  }

  async changeShoppingPurchaseStatusValidated(id: string, status: string, replacements: { itemId: string; optionId: number }[]) {
    const response = await this.authenticatedRequest("PUT", `shopping/purchases/change-status-validated/${id}/${status}`, { replacements }, { notNotify: false });
    if (response?.error) throw response;
    return response;
  }

  async changeShoppingPurchaseStatus(id, status, query, replaceBy?: GiftCardToBeBought) {
    const response = await this.authenticatedRequest("PUT", `shopping/purchases/change-status/${id}/${status}`, replaceBy ?? false, query);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getShoppingPurchaseStatus(id: string): Promise<PurchaseStatusSnapshot> {
    const response = await this.authenticatedRequest("GET", `shopping/purchases/status/${id}`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getRewards() {
    const response = await this.authenticatedRequest("GET", `rewards`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async getRewardsById(id) {
    const response = await this.authenticatedRequest("GET", `rewards/show/${id}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async postCreateRewards(data) {
    const response = await this.authenticatedRequest("POST", `rewards/create`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async putUpdateRewards(pid, data) {
    const response = await this.authenticatedRequest("PUT", `rewards/update/${pid}`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async deleteRewards(pid) {
    const response = await this.authenticatedRequest("DELETE", `rewards/delete/${pid}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getSettings() {
    const response = await this.authenticatedRequest("GET", `settings`);
    if (!response.error) {
      return response;
    }
    return false;
  }

  async putSettings(data) {
    const response = await this.authenticatedRequest("PUT", `settings`, data);
    if (!response.error) {
      return response;
    }
    return false;
  }

  async getFeed() {
    const response = await this.authenticatedRequest("GET", `feed/attitudes`);
    if (!response.error) {
      return response;
    }
    return false;
  }

  async changeRequestAttitudeStatus(id, status) {
    const response = await this.authenticatedRequest("POST", `feed/attitudes/change-status/${id}/${status}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getHistory(pid) {
    const response = await this.authenticatedRequest("GET", `players/history/${pid}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async normalizePlayerGamesDryRun(
    playerId: string,
    options: { includeCollected?: boolean; includeUncollected?: boolean } = {}
  ) {
    const response = await this.authenticatedRequest(
      "POST",
      `players/normalize-games/${playerId}`,
      false,
      {
        dryRun: "true",
        includeCollected: String(options.includeCollected !== false),
        includeUncollected: String(options.includeUncollected !== false),
      }
    );
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async normalizePlayerGamesApply(
    playerId: string,
    options: { includeCollected?: boolean; includeUncollected?: boolean } = {}
  ) {
    const response = await this.authenticatedRequest(
      "POST",
      `players/normalize-games/${playerId}`,
      false,
      {
        dryRun: "false",
        includeCollected: String(options.includeCollected !== false),
        includeUncollected: String(options.includeUncollected !== false),
      }
    );
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getStudentActivityHistorySummary(playerId: string): Promise<{
    recent: any[];
    meta: {
      oldestCollectDate: string | null;
      newestCollectDate: string | null;
      recentThrough: string;
      chunkDays: number;
      totalChunks: number;
      hasMore: boolean;
      nextBefore: string | null;
    };
    version: number;
  }> {
    const response = await this.authenticatedRequest(
      "GET",
      `students/${encodeURIComponent(playerId)}/activity-history/summary`
    );
    if (response?.error) throw new Error(String(response.error));
    return response;
  }

  async getStudentActivityHistoryChunk(
    playerId: string,
    before: string,
    loadedChunks = 0
  ): Promise<{
    transactions: any[];
    meta: {
      from: string;
      to: string;
      chunkDays: number;
      hasMore: boolean;
      nextBefore: string | null;
      loadedChunks: number;
      totalChunks: number;
    };
    version: number;
  }> {
    const response = await this.authenticatedRequest(
      "GET",
      `students/${encodeURIComponent(playerId)}/activity-history/chunk`,
      false,
      { before, loadedChunks: String(loadedChunks) }
    );
    if (response?.error) throw new Error(String(response.error));
    return response;
  }

  async getMissionHistory(pid) {
    const response = await this.authenticatedRequest("GET", `players/mission-history/${pid}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async getRewardsByGame(game) {
    const response = await this.authenticatedRequest("GET", `rewards/game/${game}`);
    if (!response.error) {
      return response;
    }
    return null;
  }

  async postApplyRewardsOnPlayer(game, playerId, data) {
    const response = await this.authenticatedRequest("POST", `rewards/game/${game}/apply/${playerId}`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postResetGames(data) {
    const response = await this.authenticatedRequest("POST", `games/reset-games-points`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async postFreezeOldGamesPoints() {
    await this.authenticatedRequest("POST", `games/freeze-old-games-points`);
  }

  async createGame(data) {
    const response = await this.authenticatedRequest("POST", `games/create-game`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async updateGame(data) {
    const response = await this.authenticatedRequest("PUT", `games/update-game`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getPreferredLanguageByEmail(email: string): Promise<SupportedLocale | null> {
    try {
      const response = await this.authenticatedRequest("GET", `users/preferred-language/${encodeURIComponent(email)}`);
      if (!response.error && response.preferredLanguage) {
        return response.preferredLanguage as SupportedLocale;
      }
    } catch {
      // endpoint indisponível ou usuário sem preferência — retorna null para usar fallback
    }
    return null;
  }

  async forgotPassword(email: string, locale?: SupportedLocale) {
    const path = `forgot-password/${encodeURIComponent(email)}`;
    const trimmed = locale?.trim();
    const hasLocale = Boolean(trimmed);
    const payload = hasLocale ? { locale: trimmed } : false;
    const query = hasLocale ? { locale: trimmed } : false;
    // locale já é transmitido via body (payload) e query string; authenticatedRequest não aceita hint extra
    const response = await this.authenticatedRequest("POST", path, payload as any, query as any);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async changePassword(uid, token, data) {
    const response = await this.authenticatedRequest("POST", `change-password/${uid}/${token}`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getUsers<T>() {
    try {
      const response = await this.authenticatedRequest("GET", `users`);
      if (!response.error) {
        return response as T;
      }
    } catch (error) {
      throw error;
    }
  }

  async getLoggedUserData() {
    const response = await this.authenticatedRequest("GET", `users/info`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getUserLevelAccess() {
    const user = await this.getLoggedUserData();

    const groups =
      Array.isArray(user.effectiveGroups) && user.effectiveGroups.length
        ? user.effectiveGroups
        : user.groups;
    const group = groups?.[0] as string;
    const accessKey = (group?.toUpperCase() || "TEACHER") as keyof typeof ACCESS_LEVELS;

    return ACCESS_LEVELS[accessKey] ?? ACCESS_LEVELS.TEACHER;
  }

  async getAccountData() {
    const response = await this.authenticatedRequest("GET", `users/account/info`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getXpResetMigration() {
    const response = await this.authenticatedRequest("GET", `users/account/xp-reset-migration`);
    if (!response.error) {
      return response as { success: boolean; xpResetMigration: any };
    }
    throw response.error;
  }

  async setXpResetMigrationDecision(decision: "reset" | "decline") {
    const response = await this.authenticatedRequest("POST", `users/account/xp-reset-migration`, { decision });
    if (!response.error) {
      return response as { success: boolean; xpResetMigration: any };
    }
    throw response.error;
  }

  async getUserById(id) {
    const response = await this.authenticatedRequest("GET", `users/find/${id}`);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async updateUser(data) {
    const response = await this.authenticatedRequest("PUT", "users/update", data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async updatePreferredLanguage(language: SupportedLocale) {
    const response = await this.authenticatedRequest("PATCH", "users/preferred-language", { language });
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async getActivityCoverDefaults(): Promise<ActivityCoverDefaults | null> {
    return requestActivityCoverDefaults(this.authenticatedRequest.bind(this));
  }

  async setActivityCoverDefaults(defaults: ActivityCoverDefaults): Promise<ActivityCoverDefaults | null> {
    return requestSetActivityCoverDefaults(this.authenticatedRequest.bind(this), defaults);
  }

  async updateAccount(data) {
    const response = await this.authenticatedRequest("PUT", "users/account/update", data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async changeGroups(id, groups) {
    const response = await this.authenticatedRequest("PUT", `users/change-group/${id}`, {
      groups,
    });
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async sendInvitation(data) {
    return this.authenticatedRequest("POST", `users/create-and-invite`, data);
  }

  async sendNewPassword(data, userId, passwordToken) {
    const response = await this.authenticatedRequest("POST", `valid-invite/${userId}/${passwordToken}`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async acceptInviteAndLogin(
    data: { newPassword: string },
    userId: string,
    passwordToken: string
  ): Promise<any> {
    const response = await this.authenticatedRequest(
      "POST",
      `valid-invite-and-login/${userId}/${passwordToken}`,
      data
    );
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getUserRemovalPreview(userId: string) {
    const response = await this.authenticatedRequest("GET", `users/remove-account/${userId}/preview`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async removeUser(userId: string, idempotencyKey: string) {
    const response = await this.authenticatedRequest(
      "POST",
      `users/remove-account/${userId}`,
      {},
      false as any,
      { "Idempotency-Key": idempotencyKey }
    );
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async registerAccount(data) {
    const response = await this.authenticatedRequest("POST", `users/register-account`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createAccount(data) {
    const response = await this.authenticatedRequest("POST", `register`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async signupRegister(data: {
    profile: "teacher" | "school";
    fullName?: string;
    schoolName?: string;
    phone?: string;
    email: string;
    /** Opcional no cadastro simplificado (so email): o backend gera a senha. */
    password?: string;
    locale?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/register", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  /**
   * Verifica o codigo do email e ja devolve a sessao (auto-login), sem senha.
   * Usado pelo cadastro simplificado; a rota legada valid-code/:userId permanece.
   */
  async verifySignupCode(userId: string, code: string) {
    const response = await this.authenticatedRequest("POST", "signup/verify-code", {
      userId,
      code,
    });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async signupRegisterWithGoogle(data: {
    profile: "teacher" | "school";
    fullName?: string;
    schoolName?: string;
    phone?: string;
    credential: string;
    locale?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/register-google", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async signupRegisterWithApple(data: {
    profile: "teacher" | "school";
    fullName?: string;
    schoolName?: string;
    phone?: string;
    code: string;
    id_token: string;
    locale?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/register-apple", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getSignupPlans(profile: "teacher" | "school") {
    const response = await this.authenticatedRequest("GET", "signup/plans", false, { profile });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getCurrentPlanLimits() {
    const response = await this.authenticatedRequest("GET", "signup/plans/limits/current");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getPlanLimitsByKey(
    planKey: string,
    options?: {
      billingCycle?: "month" | "year";
      currency?: "brl" | "eur" | "usd";
    }
  ) {
    const response = await this.authenticatedRequest("GET", `signup/plans/limits/by-plan/${planKey}`, false, options || {});
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async cancelToFree() {
    const response = await this.authenticatedRequest("POST", "signup/plans/cancel-to-free");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async cancelToFreeImmediately() {
    const response = await this.authenticatedRequest("POST", "signup/plans/cancel-to-free-now");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async revertCancelToFree() {
    const response = await this.authenticatedRequest("POST", "signup/plans/revert-cancel");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async deleteAccount() {
    const response = await this.authenticatedRequest("POST", "signup/plans/delete-account");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async deleteSchool() {
    const response = await this.authenticatedRequest("POST", "signup/plans/delete-school");
    if (!response.error) {
      return response;
    }
    throw new Error(getErrorMessage(response, "Erro ao excluir escola.")!);
  }

  async completeSignupFreePlan(profile: "teacher" | "school") {
    const response = await this.authenticatedRequest("POST", "signup/plans/free", { profile });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async startTeacherTrial(planKey: "teacher_epic" | "teacher_shiny", currency: "brl" | "eur" | "usd") {
    const response = await this.authenticatedRequest("POST", "signup/plans/teacher-trial", { currency, planKey });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async completeSchoolOnboarding(data: {
    role?: string;
    country?: string;
    phone?: string;
    schoolName?: string;
    studentCount?: string;
    goals?: string[];
    otherGoal?: string;
    completed?: boolean;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/school-onboarding", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createSignupCheckoutSession(data: {
    profile: "teacher" | "school";
    planKey: string;
    billingCycle: "month" | "year";
    currency: "brl" | "eur" | "usd";
    origin: string;
    locale?: string;
    skipTrial?: boolean;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/checkout", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createAddonCheckoutSession(data: {
    addonType: "student_pack" | "ai_unlimited";
    currency: "brl" | "eur" | "usd";
    quantity: number;
    operationId: string;
    targetSubscriptionId: string;
    origin: string;
    locale?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/addon-checkout", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async completeAddonCheckoutSession(sessionId: string) {
    const response = await this.authenticatedRequest(
      "POST",
      "signup/plans/addon-checkout/complete",
      { sessionId }
    );
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async cancelAddon(data: {
    addonType: "student_pack" | "ai_unlimited";
    targetSubscriptionId?: string;
    targetSubscriptionItemId?: string;
    targetPriceId?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/cancel-addon", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async cancelAddonV2(data: {
    addonType: "student_pack" | "ai_unlimited";
    targetSubscriptionId: string;
    targetSubscriptionItemId: string;
    targetPriceId?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/v2/cancel-addon", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createAddonDirect(data: {
    addonType: "student_pack" | "ai_unlimited";
    currency: "brl" | "eur" | "usd";
    quantity: number;
    // "set": define a quantidade em exatamente `quantity` (idempotente — "Contratar
    // IA Ilimitada" cobre N professores sem empilhar). Ausente => "increment" (legado).
    mode?: "set" | "increment";
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/addon-create-direct", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async updateAddonQuantity(data: {
    addonType: "student_pack" | "ai_unlimited";
    quantity: number;
    targetSubscriptionId?: string;
    targetSubscriptionItemId?: string;
    targetPriceId?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/update-addon-quantity", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async updateAddonQuantityV2(data: {
    addonType: "student_pack" | "ai_unlimited";
    quantity: number;
    /** A rota v2 já é exata; o marcador explicita que esta CTA nunca incrementa. */
    mode?: "set";
    targetSubscriptionId: string;
    targetSubscriptionItemId: string;
    targetPriceId?: string;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/v2/update-addon-quantity", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async syncAiAddonCoverage(data: {
    operationId: string;
    intent: "sync_to_eligible";
    targetSubscriptionId: string;
    targetSubscriptionItemId?: string;
  }) {
    const response = await this.authenticatedRequest(
      "POST",
      "signup/plans/v2/sync-ai-addon-coverage",
      data
    );
    if (!response.error) return response;
    throw response;
  }

  async completeSignupCheckoutSession(sessionId: string) {
    const response = await this.authenticatedRequest("POST", "signup/plans/checkout/complete", { sessionId });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async changeSignupTrialPlan(data: {
    subscriptionId: string;
    subscriptionItemId: string;
    targetPlanKey: string;
    billingCycle: "month" | "year";
    currency: "brl" | "eur" | "usd";
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/change-trial-plan", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async changeSignupPlan(data: {
    subscriptionId: string;
    subscriptionItemId: string;
    targetPlanKey: string;
    billingCycle: "month" | "year";
    currency: "brl" | "eur" | "usd";
    /** Obrigatorio quando a troca muda o ciclo (mensal<->anual): o backend rejeita com 400 sem isso. */
    confirmCycleChange?: boolean;
  }) {
    const response = await this.authenticatedRequest("POST", "signup/plans/change-plan", data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async verifyUserWithCode(userId, code, email, password) {
    const response = await this.authenticatedRequest("POST", `valid-code/${userId}`, {
      code,
      email,
      password,
    });
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async resendInvite(userId) {
    const response = await this.authenticatedRequest("POST", `users/${userId}/resend-invite`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async generateImpersonationToken(userId) {
    const response = await this.authenticatedRequest("POST", `users/generate-impersonation-token/${userId}`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async sendCode(userId) {
    const response = await this.authenticatedRequest("POST", `send-code/${userId}`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createClassTeacher(data) {
    const response = await this.authenticatedRequest("PUT", `users/update-classes`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getClassesByUserId(userId) {
    const response = await this.authenticatedRequest("GET", `users/classes/${userId}`);
    if (response == null) {
      return { classes: [] };
    }
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getAllTeacherClassesPermissions() {
    const response = await this.authenticatedRequest("GET", `users/classes/all`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getClassesByTeacher() {
    const response = await this.authenticatedRequest("GET", `classes/teacher`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createRecurrence(data) {
    const normalizedPayload = {
      ...data,
      mustBeCollected: Boolean(data?.mustBeCollected ?? data?.recurrence?.mustBeCollected),
    };
    const response = await this.authenticatedRequest("POST", `recurrence/create`, normalizedPayload);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getRecurrence(attitudeId) {
    const response = await this.authenticatedRequest("GET", `recurrence/${attitudeId}`);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async editRecurrence(id, data) {
    const normalizedPayload = {
      ...data,
      mustBeCollected: Boolean(data?.mustBeCollected ?? data?.recurrence?.mustBeCollected),
    };
    const response = await this.authenticatedRequest("PUT", `recurrence/update/${id}`, normalizedPayload);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async deleteRecurrence(id) {
    const response = await this.authenticatedRequest("DELETE", `recurrence/delete/${id}`);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createResetGameRecurrence(data) {
    const response = await this.authenticatedRequest("POST", `recurrence-reset-games/create`, data);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async editResetGameRecurrence(id, data) {
    const response = await this.authenticatedRequest("PUT", `recurrence-reset-games/update/${id}`, data);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getResetGameRecurrence() {
    const response = await this.authenticatedRequest("GET", `recurrence/all`);

    if (!response.error) {
      return response;
    }
    throw response;
  }
  async getGiftCards() {
    const response = await this.authenticatedRequest("GET", `shopping/giftcards`);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getRecurrenceAnalisis(): Promise<RecurrenceAnalysisResponse> {
    const response = await this.authenticatedRequest("GET", `recurrence/analisis`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getGiftCardsAvailabes() {
    const response = await this.authenticatedRequest("GET", `shopping/giftcards/availables`);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async buyGiftCards(payload) {
    const response = await this.authenticatedRequest("POST", `shopping/giftcards/buy`, payload);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async generateQrCodes(world, payload) {
    // A rota responde um ZIP binário (Content-Disposition attachment) — o
    // authenticatedRequest tentava response.json() e "errava" mesmo com 200,
    // com os hashes já gerados no servidor. Fetch direto, sem parse de JSON.
    const token = AuthService.isLogged();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = token;
    const response = await fetch(`${BASE_URL}/${world}/qrcode`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`generateQrCodes failed: ${response.status}`);
    }
    return response.blob();
  }
  async requestQrCodesZip(world: string, payload: { ids: string[] }, overwrite: boolean): Promise<Blob> {
    // Dispara a geração do ZIP de QR codes no servidor. O estado "pronto" do
    // chamador vem do polling do histórico (type "generate_qrcode" com a URL
    // do S3) — em mundos grandes a resposta síncrona pode estourar o timeout
    // do gateway; por isso o status é exposto no erro para o chamador
    // distinguir 4xx de timeout. Quando a resposta síncrona completa, o blob
    // (o próprio ZIP) é devolvido para reuso local sem novo download do S3.
    const token = AuthService.isLogged();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = token;
    const path = overwrite ? "qrcode" : "qrcode/download";
    const response = await fetch(`${BASE_URL}/${world}/${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error: Error & { status?: number } = new Error(`requestQrCodesZip failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.blob();
  }

  async downloadQrCodes(world, payload) {
    // Mesmo caso do generateQrCodes: resposta é um ZIP binário. Baixa o
    // arquivo de verdade (antes o parse de JSON estourava e nada era salvo).
    const token = AuthService.isLogged();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = token;
    const response = await fetch(`${BASE_URL}/${world}/qrcode/download`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`downloadQrCodes failed: ${response.status}`);
    }
    const blob = await response.blob();
    if (typeof window !== "undefined") {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "qrcodes.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
    return blob;
  }

  async downloadAccessInstructionsXls(playerIds: string[]): Promise<void> {
    const token = AuthService.isLogged();
    if (!token) {
      throw new Error("Token de autenticação não encontrado");
    }

    const response = await fetch(`${this.baseUrl}/players/access-instructions/xls`, {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ playerIds }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(err.message || "Falha ao gerar XLS");
    }

    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition");
    const filename =
      disposition?.match(/filename="?([^";]+)"?/)?.[1] ||
      `instrucoes-acesso-${new Date().toISOString().slice(0, 10)}.xls`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async startBlankQrCodesJob(amount: number): Promise<{ jobId: string }> {
    const response = await this.authenticatedRequest(
      "POST",
      "authenticated/generate/qrcodes/jobs",
      { amount }
    );
    if (response?.error || typeof response?.jobId !== "string") {
      throw new Error("Falha ao iniciar a geração dos QR Codes");
    }
    return { jobId: response.jobId };
  }

  async getBlankQrCodesJob(jobId: string): Promise<BlankQrCodeJobSnapshot> {
    const response = await this.authenticatedRequest("GET", `jobs/${encodeURIComponent(jobId)}`);
    if (response?.error || typeof response?.status !== "string") {
      throw new Error("Falha ao consultar a geração dos QR Codes");
    }
    return response as BlankQrCodeJobSnapshot;
  }

  async generateBlankQrCodes(amount: number): Promise<void> {
    const token = AuthService.isLogged();
    const response = await fetch(
      `${this.baseUrl}/authenticated/generate/qrcodes?amount=${encodeURIComponent(String(amount))}`,
      {
        method: "GET",
        headers: token ? { Authorization: token } : {},
      }
    );

    if (!response.ok) {
      throw new Error(`Falha ao gerar QR Codes (${response.status})`);
    }
  }

  async refundGiftCards(payload) {
    const response = await this.authenticatedRequest("POST", `shopping/giftcards/refund`, payload);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getWallet() {
    const response = await this.authenticatedRequest("GET", `shopping/wallet`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getWalletStatment(limit) {
    let url = `shopping/wallet/statements`;
    if (limit) {
      url = `${url}?limit=${limit}`;
    }
    const response = await this.authenticatedRequest("GET", url);

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getPayments() {
    const response = await this.authenticatedRequest("GET", `shopping/payments`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getPaymentsById(id) {
    const response = await this.authenticatedRequest("GET", `shopping/payments/show/${id}`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async paymentsCreate(data) {
    const response = await this.authenticatedRequest("POST", `shopping/payments/create`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async paymentsCreateCreditCard(data) {
    const response = await this.authenticatedRequest("POST", `shopping/payments/creditcard`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async paymentsStatus(data) {
    const response = await this.authenticatedRequest("POST", `shopping/payments/notification`, data);
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async sendToShopping(payload) {
    const response = await this.authenticatedRequest("POST", `shopping/giftcards/sendshop`, payload);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async sendToShoppingPostPaid(payload) {
    const response = await this.authenticatedRequest("POST", `shopping/giftcards/sendshop-postpaid`, payload);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async cartCheckout(payload) {
    const response = await this.authenticatedRequest("POST", `shopping/giftcards/cart-checkout`, payload);
    if (!response.error) { return response; }
    throw response;
  }

  async sendEmailPayment({ type, paymentId, email }) {
    const response = await this.authenticatedRequest("POST", `shopping/payments/sendemail/${type}/${paymentId}`, { email });

    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getCategoryRewards() {
    const response = await this.authenticatedRequest("GET", `shopping/item-category`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async categoryCreate(payload) {
    const response = await this.authenticatedRequest("POST", `shopping/item-category/create`, payload);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getIconsRewards() {
    const response = await this.authenticatedRequest("GET", `shopping/items/image/list`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async removeOrAddCurrency(payload) {
    const response = await this.authenticatedRequest("POST", "bonus/apply-balance-change?notNotify=false&", payload);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async postProofs(payload) {
    const response = await this.authenticatedRequest("POST", "missoes2/comprovantes/comprovar", payload);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  // criar um endpoints para trazer so os do usuario
  async getAllReceipts(): Promise<Proofs[]> {
    const response = await this.authenticatedRequest("GET", "missoes2/comprovantes/get-all");
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async sendErrorToSuport(payload: { worldId: string; accountId: string; page: string; browser: string; error: string }) {
    await this.authenticatedRequest("POST", "send-error-to-support", payload);
  }

  async getCoinsUsedByTeacher(id: string) {
    const response = await this.authenticatedRequest("GET", `missoes2/coins-used/${id}`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getTeacherCoinLimits(): Promise<TeacherCoinLimitSnapshot> {
    const response = await this.authenticatedRequest("GET", "teacher-coin-limits");
    if (!response.error) {
      return response as TeacherCoinLimitSnapshot;
    }
    throw response.error;
  }

  async previewTeacherCoinLimits(input: TeacherCoinLimitPreviewInput): Promise<TeacherCoinLimitSnapshot> {
    const response = await this.authenticatedRequest("POST", "teacher-coin-limits/preview", input);
    if (!response.error) {
      return response as TeacherCoinLimitSnapshot;
    }
    throw response.error;
  }

  async getCoinsUsedPerClass() {
    const response = await this.authenticatedRequest("GET", `dashboard/coins-per-class`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getEngagmentPerYear() {
    const response = await this.authenticatedRequest("GET", `dashboard/engagement-per-year`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getMissionDataForChart() {
    const response = await this.authenticatedRequest("GET", `dashboard/missions`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getDashboardRewards() {
    const response = await this.authenticatedRequest("GET", `dashboard/rewards`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getDashboardMostRedeemedRewards() {
    const response = await this.authenticatedRequest("GET", `dashboard/most-redeemed-rewards`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getDashboardTeacherUsage() {
    const response = await this.authenticatedRequest("GET", `dashboard/teacher-usage`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getDashboardUserAccess() {
    const response = await this.authenticatedRequest("GET", `dashboard/user-access`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getDashboardMonthlyAccess() {
    const response = await this.authenticatedRequest("GET", `dashboard/total-access`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getDashboardAnaliseConta() {
    const response = await this.authenticatedRequest("GET", `dashboard/analytics-conta`);

    if (!response.error) {
      return response;
    }

    throw response;
  }

  async getDashboardPlayersAccess() {
    const response = await this.authenticatedRequest("GET", `dashboard/players-access`);

    if (!response.error) {
      return response as Array<{ playerId: string; firstName?: string; surname?: string; lastCollectDate: string | null }>;
    }

    throw response;
  }

  async deleteQuiz(id: string) {
    const response = await this.authenticatedRequest("DELETE", `quizzes/delete/${id}`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async createQuizzFromImages(title: string, imagesUrls: string[]) {
    const response = await this.authenticatedRequest("POST", `quizzes/create-from-images`, { title, imagesUrls });
    if (!response.error) {
      return response;
    }
    throw response.error;
  }

  async aiCreateQuizV2Async(
    payload: { files?: File[]; prompt: string; questionType?: string; numberOfQuestions?: number; difficulty?: string; educationLevel?: string },
    onProgress?: (info: { step: string; message: string; progress: number; agentActions?: any[] }) => void
  ): Promise<{ success: boolean; quiz?: any; reasoning?: string; agentActions?: any[]; iterationCount?: number; metadata?: any; error?: string; message?: string }> {
    const form = new FormData();
    form.append("prompt", payload.prompt ?? "");
    form.append("questionType", payload.questionType ?? "multiple");
    form.append("numberOfQuestions", String(payload.numberOfQuestions ?? 8));
    form.append("difficulty", payload.difficulty ?? "medio");
    form.append("educationLevel", payload.educationLevel ?? "");
    if (payload.files) {
      for (const file of payload.files) {
        form.append("files", file);
      }
    }

    const axiosResponse = await this.authenticatedRequestOnAxios({ method: "POST", path: "ai/v2/create-quiz-async", data: form, headers: { "Content-Type": "multipart/form-data" } });
    const startData = typeof axiosResponse.data === "string" ? JSON.parse(axiosResponse.data) : axiosResponse.data;
    const jobId: string | undefined = startData?.jobId;

    if (!jobId) {
      return { success: false, error: "NO_JOB_ID" };
    }

    const POLL_INTERVAL = 800;
    const MAX_POLLS = 600;

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));

      const pollResponse = await this.authenticatedRequest("GET", `ai-jobs/${jobId}`);

      if (pollResponse?.status === "processing") {
        if (onProgress && pollResponse.progress) {
          onProgress({
            step: pollResponse.progress.step ?? "",
            message: pollResponse.progress.message ?? "",
            progress: pollResponse.progress.progress ?? 0,
            agentActions: pollResponse.progress.agentActions,
          });
        }
        continue;
      }

      if (pollResponse?.status === "completed") {
        const data = pollResponse.data ?? {};
        if (onProgress) {
          onProgress({ step: "done", message: "Quiz criado!", progress: 100, agentActions: data.agentActions });
        }
        return data;
      }

      if (pollResponse?.status === "error") {
        return { success: false, error: pollResponse.error || "QUIZ_AGENT_FAILED", message: pollResponse.data?.message };
      }
    }

    return { success: false, error: "TIMEOUT" };
  }

  async aiCreateMissionV2Async(
    payload: { files?: File[]; prompt: string; contexto?: string; faixa_etaria?: string; nivel_dificuldade?: string; games?: string[]; turmas?: string[]; aiWillReward?: boolean },
    onProgress?: (info: { step: string; message: string; progress: number; agentActions?: any[] }) => void
  ): Promise<{ success: boolean; mission?: any; variants?: any[]; analysisReport?: any; agentActions?: any[]; iterationCount?: number; error?: string; message?: string }> {
    const form = new FormData();
    form.append("prompt", payload.prompt ?? "");
    if (payload.contexto) form.append("contexto", payload.contexto);
    if (payload.faixa_etaria) form.append("faixa_etaria", payload.faixa_etaria);
    if (payload.nivel_dificuldade) form.append("nivel_dificuldade", payload.nivel_dificuldade);
    if (payload.games) form.append("games", JSON.stringify(payload.games));
    if (payload.turmas) form.append("turmas", JSON.stringify(payload.turmas));
    if (payload.aiWillReward) form.append("aiWillReward", "true");
    if (payload.files) {
      for (const file of payload.files) form.append("files", file);
    }

    const axiosResponse = await this.authenticatedRequestOnAxios({ method: "POST", path: "ai/v2/create-mission-async", data: form, headers: { "Content-Type": "multipart/form-data" } });
    const startData = typeof axiosResponse.data === "string" ? JSON.parse(axiosResponse.data) : axiosResponse.data;
    const jobId: string | undefined = startData?.jobId;
    if (!jobId) return { success: false, error: "NO_JOB_ID" };

    const POLL_INTERVAL = 800;
    const MAX_POLLS = 700;

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));
      const pollResponse = await this.authenticatedRequest("GET", `ai-jobs/${jobId}`);

      if (pollResponse?.status === "processing") {
        if (onProgress && pollResponse.progress) {
          onProgress({ step: pollResponse.progress.step ?? "", message: pollResponse.progress.message ?? "", progress: pollResponse.progress.progress ?? 0, agentActions: pollResponse.progress.agentActions });
        }
        continue;
      }
      if (pollResponse?.status === "completed") {
        const data = pollResponse.data ?? {};
        if (onProgress) onProgress({ step: "done", message: "Missao criada!", progress: 100, agentActions: data.agentActions });
        return data;
      }
      if (pollResponse?.status === "error") {
        return { success: false, error: pollResponse.error || "MISSION_AGENT_FAILED", message: pollResponse.data?.message };
      }
    }
    return { success: false, error: "TIMEOUT" };
  }

  async aiSelectStudents(payload: { file?: File; prompt: string; students: Array<{ id: string; name: string }> }) {
    const form = new FormData();
    form.append("prompt", payload.prompt ?? "");
    form.append("students", JSON.stringify(Array.isArray(payload.students) ? payload.students : []));
    if (payload.file) {
      form.append("file", payload.file);
    }
    const axiosResponse = await this.authenticatedRequestOnAxios({ method: "POST", path: "ai/select-students", data: form, headers: { "Content-Type": "multipart/form-data" } });
    try {
      const data = typeof axiosResponse.data === "string" ? JSON.parse(axiosResponse.data) : axiosResponse.data;
      return data;
    } catch {
      return { success: false, error: "INVALID_RESPONSE" } as any;
    }
  }

  async aiSelectStudentsAsync(
    payload: { file?: File; prompt: string; students: Array<{ id: string; name: string }> },
    onProgress?: (info: { step: string; message: string; progress: number }) => void
  ): Promise<{ success: boolean; selectedIds?: string[]; reasoning?: string; usedDeterministicCode?: boolean; method?: string; error?: string; message?: string }> {
    const form = new FormData();
    form.append("prompt", payload.prompt ?? "");
    form.append("students", JSON.stringify(Array.isArray(payload.students) ? payload.students : []));
    if (payload.file) {
      form.append("file", payload.file);
    }

    const axiosResponse = await this.authenticatedRequestOnAxios({ method: "POST", path: "ai/select-students-async", data: form, headers: { "Content-Type": "multipart/form-data" } });
    const startData = typeof axiosResponse.data === "string" ? JSON.parse(axiosResponse.data) : axiosResponse.data;
    const jobId: string | undefined = startData?.jobId;

    if (!jobId) {
      return { success: false, error: "NO_JOB_ID" };
    }

    const POLL_INTERVAL = 1200;
    const MAX_POLLS = 300;

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));

      const pollResponse = await this.authenticatedRequest("GET", `ai-jobs/${jobId}`);

      if (pollResponse?.status === "processing") {
        if (onProgress && pollResponse.progress) {
          onProgress({
            step: pollResponse.progress.step ?? "",
            message: pollResponse.progress.message ?? "",
            progress: pollResponse.progress.progress ?? 0,
            partialData: pollResponse.progress.partialData,
          } as any);
        }
        continue;
      }

      if (pollResponse?.status === "completed") {
        const data = pollResponse.data ?? {};
        if (onProgress) {
          onProgress({ step: "done", message: "Seleção concluída!", progress: 100 } as any);
        }
        return data;
      }

      if (pollResponse?.status === "error") {
        return { success: false, error: pollResponse.error || "AI_PROCESSING_FAILED", message: pollResponse.data?.message };
      }
    }

    return { success: false, error: "TIMEOUT" };
  }

  async aiSelectStudentsV2Async(
    payload: { file?: File; prompt: string; students: Array<{ id: string; name: string }> },
    onProgress?: (info: { step: string; message: string; progress: number; agentActions?: any[] }) => void
  ): Promise<{ success: boolean; selectedIds?: string[]; reasoning?: string; agentActions?: any[]; iterationCount?: number; method?: string; error?: string; message?: string }> {
    const form = new FormData();
    form.append("prompt", payload.prompt ?? "");
    form.append("students", JSON.stringify(Array.isArray(payload.students) ? payload.students : []));
    if (payload.file) {
      form.append("file", payload.file);
    }

    const axiosResponse = await this.authenticatedRequestOnAxios({ method: "POST", path: "ai/v2/select-students-async", data: form, headers: { "Content-Type": "multipart/form-data" } });
    const startData = typeof axiosResponse.data === "string" ? JSON.parse(axiosResponse.data) : axiosResponse.data;
    const jobId: string | undefined = startData?.jobId;

    if (!jobId) {
      return { success: false, error: "NO_JOB_ID" };
    }

    const POLL_INTERVAL = 800;
    const MAX_POLLS = 500;

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));

      const pollResponse = await this.authenticatedRequest("GET", `ai-jobs/${jobId}`);

      if (pollResponse?.status === "processing") {
        if (onProgress && pollResponse.progress) {
          onProgress({
            step: pollResponse.progress.step ?? "",
            message: pollResponse.progress.message ?? "",
            progress: pollResponse.progress.progress ?? 0,
            agentActions: pollResponse.progress.agentActions,
          });
        }
        continue;
      }

      if (pollResponse?.status === "completed") {
        const data = pollResponse.data ?? {};
        if (onProgress) {
          onProgress({ step: "done", message: "Agente concluiu!", progress: 100, agentActions: data.agentActions });
        }
        return data;
      }

      if (pollResponse?.status === "error") {
        return { success: false, error: pollResponse.error || "AGENT_PROCESSING_FAILED", message: pollResponse.data?.message };
      }
    }

    return { success: false, error: "TIMEOUT" };
  }

  async aiParsePlayersFile(file: File): Promise<{ ok: boolean; fileUrl?: string; data?: Array<{ nome: string; turma?: string }>; error?: string }>
  {
    const form = new FormData();
    form.append("file", file);
    const axiosResponse = await this.authenticatedRequestOnAxios({ method: "POST", path: "ai/parse-players-file", data: form, headers: { "Content-Type": "multipart/form-data" } });
    try {
      const data = typeof axiosResponse.data === "string" ? JSON.parse(axiosResponse.data) : axiosResponse.data;
      return data;
    } catch {
      return { ok: false, error: "INVALID_RESPONSE" } as any;
    }
  }

  // Streaming version for real-time progress updates
  async aiParsePlayersFileStream(file: File): Promise<Response> {
    const form = new FormData();
    form.append("file", file);
    
    const token = AuthService.isLogged();
    if (!token) {
      throw new Error("Token de autenticação não encontrado");
    }

    const response = await fetch(`${this.baseUrl}/ai/parse-players-file`, {
      method: "POST",
      headers: {
        Authorization: token,
      },
      body: form,
    });

    return response;
  }

  // Streaming version with CODEGEN approach (LLM generates transformation code)
  async aiParsePlayersFileCodegenStream(file: File): Promise<Response> {
    const form = new FormData();
    form.append("file", file);
    
    const token = AuthService.isLogged();
    if (!token) {
      throw new Error("Token de autenticação não encontrado");
    }

    const response = await fetch(`${this.baseUrl}/ai/parse-players-file-codegen`, {
      method: "POST",
      headers: {
        Authorization: token,
      },
      body: form,
    });

    return response;
  }

  async authenticatedSSERequest<T = any>({
    method,
    path,
    data,
    onProgress,
  }: {
    method: "POST" | "PUT";
    path: string;
    data?: any;
    onProgress?: (info: { step: string; message: string; progress: number; partialData?: any }) => void;
  }): Promise<T> {
    const submitResponse = await this.authenticatedRequest(method, path, data);

    if (submitResponse?.error) {
      throw new Error(submitResponse.error?.message || submitResponse.error || "Erro na requisição");
    }

    const jobId = submitResponse?.jobId;
    if (!jobId) {
      return submitResponse as T;
    }

    const POLL_INTERVAL = 1500;
    const MAX_POLLS = 400;

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));

      const pollResponse = await this.authenticatedRequest("GET", `ai-jobs/${jobId}`);

      if (pollResponse?.status === "processing") {
        if (onProgress && pollResponse.progress) {
          onProgress(pollResponse.progress);
        }
        continue;
      }

      if (pollResponse?.status === "completed") {
        if (onProgress) {
          onProgress({ step: "done", message: "Concluído!", progress: 100 });
        }
        return pollResponse.data as T;
      }

      if (pollResponse?.status === "error") {
        throw new Error(pollResponse.error || "Erro no processamento");
      }

      if (pollResponse?.status === "not_found") {
        throw new Error("Job não encontrado no servidor");
      }
    }

    throw new Error("Tempo limite excedido aguardando processamento");
  }

  async updateQuiz(id: string, data: IQuizz) {
    const response = await this.authenticatedRequest("POST", `quizzes/update/${id}`, data);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getDiscursiveQuizStats(quizId: string) {
    const response = await this.authenticatedRequest("GET", `quizzes/${quizId}/discursive-stats`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getDiscursiveComprovantes(quizId: string) {
    const response = await this.authenticatedRequest("GET", `quizzes/${quizId}/discursive-comprovantes`);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async revisarResultsDiscursivas(payload: { comprovanteId: string; questions: { status: "certo" | "errado" | "incompleto" }[] }) {
    const response = await this.authenticatedRequest("POST", `quizzes/revisar-results-discursivas`, payload as any);
    if (!response.error) {
      return response;
    }
    throw response;
  }

  async getTransactionsByKhanDaily(fileDate: string) {
    const response = await this.authenticatedRequest("GET", `transactions/khan-daily`, false, { fileDate });
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getTransactionsByKhanWeekly(weekId: string) {
    const response = await this.authenticatedRequest("GET", `transactions/khan-weekly`, false, { weekId });
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getTransactionsByKhanMonthly(monthKey: string) {
    const response = await this.authenticatedRequest("GET", `transactions/khan-monthly`, false, { monthKey });
    if (!response.error) {
      return response;
    }
    return [];
  }

  async getTransactionsByKhanWeeklySkills(weekId: string) {
    const response = await this.authenticatedRequest("GET", `transactions/khan-weekly-skills`, false, { weekId });
    if (!response.error) {
      return response;
    }
    return [];
  }

  async createPixPayment(invoiceId: string): Promise<{ payload: string; encodedImage: string; data_expiracao: string; id_asaas: string }> {
    const response = await this.authenticatedRequest("POST", `billing/invoices/${invoiceId}/create-pix`);
    if (!response.error && response.success) {
      return response.data;
    }
    throw new Error(response.error || "Erro ao criar pagamento PIX");
  }

  async checkPixPaymentStatus(invoiceId: string): Promise<{ paid: boolean; paidAt?: string }> {
    const response = await this.authenticatedRequest("GET", `billing/invoices/${invoiceId}/pix-status`);
    if (!response.error && response.success) {
      return response.data;
    }
    throw new Error(response.error || "Erro ao verificar status do pagamento");
  }

  // XP Toggle methods
  async getXpToggleStatus(): Promise<{
    success: boolean;
    xpEnabled: boolean;
    lastDisabledAt: string | null;
    lastDisabledBy: string | null;
    batchId: string | null;
    entriesCount: number;
  }> {
    const response = await this.authenticatedRequest("GET", "xp-toggle/status");
    if (!response.error) {
      return response;
    }
    throw new Error(response.error || "Erro ao verificar status do XP");
  }

  async disableXp(): Promise<{
    success: boolean;
    batchId: string;
    entriesCount: number;
    summary: {
      attitudes: number;
      missions: number;
      quizzes: number;
    };
  }> {
    const response = await this.authenticatedRequest("POST", "xp-toggle/disable");
    if (!response.error && response.success) {
      return response;
    }
    throw new Error(response.message || response.error || "Erro ao desativar XP");
  }

  async enableXp(batchId?: string): Promise<{
    success: boolean;
    batchId: string;
    restoredCount: number;
    originalEntriesCount: number;
  }> {
    const response = await this.authenticatedRequest("POST", "xp-toggle/enable", batchId ? { batchId } : {});
    if (!response.error && response.success) {
      return response;
    }
    throw new Error(response.message || response.error || "Erro ao ativar XP");
  }

  async getPendingChestsByWorld(): Promise<Array<{ playerId: string; count: number }>> {
    const response = await this.authenticatedRequest("GET", "chests/pending-count-by-world");
    if (Array.isArray(response)) return response;
    return [];
  }

  async grantChests({ playerIds, quantity }: { playerIds: string[]; quantity: number }): Promise<any> {
    const response = await this.authenticatedRequest("POST", "chests/grant", { playerIds, quantity });
    if (response && !response.error) {
      return response;
    }
    throw new Error(response?.message || response?.error || "Erro ao conceder baús");
  }

  async getGlobalLevels(): Promise<Array<{ level: number; xpRequired: number }>> {
    const response = await this.authenticatedRequest("GET", "levels");
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  async getPlayersSelectedHeroes(playerIds: string[]): Promise<Record<string, any>> {
    const response = await this.authenticatedRequest("POST", "game/players/selected-heroes", { playerIds });
    if (response && !response.error) {
      return response;
    }
    return {};
  }
}

interface RecurrenceAnalysisRecurrence {
  id: string;
  attitudeId: string;
  isActive: boolean;
  typeRecurrence: string;
  createdAt: Date;
  world: string;
}

interface RecurrenceAnalysisAttitude {
  id: string;
  name: string;
  desc: string;
}

interface MonthlyCoinsDistribution {
  [monthKey: string]: number; // e.g., "2025-05": 880000
}

export interface RecurrenceAnalysisResult {
  recurrence: RecurrenceAnalysisRecurrence;
  attitude: RecurrenceAnalysisAttitude | null;
  monthlyCoinsDistribution: MonthlyCoinsDistribution;
  totalCoinsDistributed: number;
  totalTransactions: number;
}

export type RecurrenceAnalysisResponse = RecurrenceAnalysisResult[];

export type AnaliseConta = {
  date: string;
  ticker: string;
  usuarios_cadastrados: number;
  porcentagem_de_alunos_que_compraram_nos_ultimos_15_dias: number;
  valor_total_em_coins_das_recompensas: number;
  numero_recompensas: number;
  ultimo_dia_de_uso: string;
  nome: string;
  numero_missoes2: number;
  valor_total_em_coins_da_economia: number;
  numero_de_usuarios: number;
  ultima_fatura: string;
  numero_pedidos: number;
  quantidade_de_usos_no_ultimo_mes: number;
  ultima_fatura_status: string;
  game_controlado: string;
  numero_estoque_de_recompensas: number;
  MRR: number;
  LTV: number;
  numero_jogadores_cadastrados: number;
  porcentagem_de_alunos_que_resgataram_saldo_nos_ultimos_15_dias: number;
  porcentagem_de_alunos_que_compraram_gift_card_nos_ultimos_15_dias: number;
  tipo: string;
  valor_total_em_coins_gastas_na_loja: number;
  numero_de_games: number;
  porcentagem_de_alunos_que_receberam_atitudes_nos_ultimos_15_dias: number;
  _id: string;
  id: string;
  numero_attitudes: number;
  porcentagem_de_alunos_que_receberam_missões_nos_ultimos_15_dias: number;
  numero_de_turmas: number;
  media_de_coins_por_aluno: number;
  media_de_coins_do_top_10_porcento_por_aluno: number;
  porcentagem_de_alunos_que_resgataram_missões_nos_ultimos_15_dias: number;
};
